import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';

const columns = [
  {
    title: 'Marketing agency / production company',
    points: [
      'Beautiful, TV-style presentation videos that do not hold attention in today’s short-form social feeds.',
      '$5,000–$10,000 for one or two generic videos that are not tailored to your brand.',
      '“Ad roulette”: recycled concepts, often subcontracted to the cheapest available videographer.',
      'Long contracts and broad service menus instead of deep video ad expertise.',
      'Often remote, with little interest in meeting or filming with you in person.',
    ],
  },
  {
    title: 'UGC creator / freelancer',
    points: [
      'A one-person show expected to research formats, find the angle, script, film, perform on camera, edit and deliver.',
      'A generalist doing several specialist jobs, making consistency, speed and high quality hard to sustain.',
      'No production crew or project manager keeping every detail and deadline accountable.',
      'May be remote and unwilling to meet or shoot with you in person.',
    ],
  },
  {
    title: 'Doing it yourself',
    points: [
      'Consistency disappears when filming competes with running your business.',
      'You need the right equipment and training in formats, angles, scripting, filming, acting and editing.',
      'You must stay on top of every new platform format and trend.',
      'It takes significant time and energy because it is a multi-person job—not a side task.',
    ],
  },
];

const taoh = [
  'A full in-house team of specialists—not one generalist doing every job.',
  'Strategists find the right format and marketing angle; copywriters turn it into a performance-focused script.',
  'A professional crew directs and films, with specialists behind the camera and talent in front of it.',
  'Professional editors finish every video while project managers control quality, speed and deadlines.',
  'We understand the hooks, formats and marketing angles that make ads perform on Reels, TikTok and Shorts.',
  'Every concept is personalized to your brand, customers and market—never a generic template.',
  'One coordinated team that scripts, films and edits with you.',
  'Everything stays in-house. We never subcontract to cheap videographers or freelancers.',
  'Your first versions are delivered within 10 days after the video shoot.',
];

export const Comparison = () => (
  <section className="section">
    <div className="container mx-auto max-w-6xl px-4">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
          Why pick <span className="text-gradient">TAOH</span> over the alternatives
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          We collapse the whole value chain into one local, hands-off, performance-focused package.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.08fr_1fr] items-start">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="rounded-lg border border-accent/60 bg-accent/20 p-5 md:p-7 shadow-sm"
        >
          <h3 className="text-2xl font-bold mb-5">The Alpha Omega Hub</h3>
          <ul className="space-y-3">
            {taoh.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <Check className="w-5 h-5 shrink-0 mt-0.5 text-primary" />
                <span className="text-foreground/90">{p}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        <div className="grid gap-4">
          {columns.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="rounded-lg border border-destructive/20 bg-destructive/5 p-5 md:p-6 shadow-sm"
            >
              <h3 className="text-lg font-bold mb-4">{c.title}</h3>
              <ul className="space-y-2.5">
                {c.points.map((p) => (
                  <li key={p} className="flex items-start gap-2.5">
                    <X className="w-4 h-4 shrink-0 mt-1 text-destructive" />
                    <span className="text-sm text-muted-foreground">{p}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);